import { ReactNode, useEffect, useMemo, useState, useCallback } from "react";
import { AppContext } from "./AppContext";
import { isCurrentTimeInSchedule } from "../utils";
import { validateEmail } from "../utils/emailValidation";
import { validatePhone } from "../utils/phoneValidation";
import React from "react";
import { CartPosition, Branch, DeliveryAddress, DeliveryMethod, DeliveryType, DeliveryTimeType, MenuPage } from "../types/cart.types";
import { setApiSlug } from "../service/client";
import { legacyPathToPage } from "../utils/navigation";

export const AppContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  // restaurant slug from URL
  const [restaurantSlug, setRestaurantSlugState] = useState<string | null>(null);

  // Setter that also syncs slug to the axios interceptor
  const setRestaurantSlug = useCallback((slug: string | null) => {
    setRestaurantSlugState(slug);
    setApiSlug(slug);
  }, []);

  // branch restauracji
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [isOpen, setIsOpen] = useState<boolean | undefined>();

  // koszyk
  const [cart, setCart] = useState<CartPosition[]>([]);
  // czas dla usług i typ dostawy dla zamówień
  const [additionalSections, setAdditionalSections] = useState(null);
  const [deliveryType, setDeliveryType] = useState<DeliveryType>("TO_THE_ADDRESS");
  const [deliveryTimeType, setDeliveryTimeType] = useState<DeliveryTimeType>("NOW");
  const [orderDate, setOrderDate] = useState<string | null>(null);
  const [orderTime, setOrderTime] = useState<string | null>(null);
  // czas dla usług
  const [serviceData, setServiceData] = useState<any>(null);
  const [serviceTime, setServiceTime] = useState<any>(null);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [serviceResurces, setServiceResurces] = useState<Record<string, any>>({});
  // google maps — address is restored per-branch in useAddressAutocomplete
  const [deliveryAdress, setDeliveryAdress] = useState<DeliveryAddress | null>(null);
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<DeliveryMethod | null>(null);
  const initApertmenNumber = localStorage.getItem("apertmenNumber");
  const [apertmenNumber, setApertmenNumber] = useState<string>(
    initApertmenNumber ?? ""
  );
  const initDdeliveryNote = localStorage.getItem("deliveryNote");
  const [deliveryNote, setDeliveryNote] = useState<string>(
    initDdeliveryNote ?? ""
  );
  // dane zawsze obowiązkowe
  const initName = localStorage.getItem("name");
  const [name, setName] = useState<string>(initName ?? "");
  const initEmail = localStorage.getItem("email");
  const [email, setEmail] = useState<string>(initEmail ?? "");
  const initNumber = localStorage.getItem("number");
  const [number, setNumber] = useState<string>(initNumber ?? "");
  // sterowanie przyciskiem w headerze
  const [isMain, setIsMain] = useState<boolean>(true);
  const [mealName, setMealName] = useState<string>("");

  // coupon
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [couponCode, setCouponCode] = useState<string>("");
  const [scroll, setScroll] = useState<number>(0);

  // Order flow flag (true = product order, false = service booking)
  const [isOrder, setIsOrder] = useState<boolean>(true);

  // Search
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Page navigation
  const [currentPage, setCurrentPage] = useState<MenuPage>({ page: 'dishes' });
  const [, setPageHistory] = useState<MenuPage[]>([]);

  const navigateTo = useCallback((page: MenuPage) => {
    setCurrentPage(prev => {
      setPageHistory(h => [...h, prev]);
      return page;
    });
  }, []);

  const goBack = useCallback(() => {
    setPageHistory(prev => {
      if (prev.length === 0) return prev;
      const newHistory = [...prev];
      const previousPage = newHistory.pop()!;
      setCurrentPage(previousPage);
      return newHistory;
    });
  }, []);

  // Memoized total amount calculation
  const total_ammount = useMemo(
    () =>
      cart
        .map(
          (position) =>
            position.cart_positoin.meal_price *
            position.cart_positoin.meal_quantity
        )
        .reduce((accumulator, currentValue) => accumulator + currentValue, 0),
    [cart]
  );

  // Walidacja email
  const { isValid: isEmailValid, error: emailError } = useMemo(() => {
    return validateEmail(email);
  }, [email]);

  // Walidacja telefonu
  const { isValid: isPhoneValid, error: phoneError } = useMemo(() => {
    return validatePhone(number);
  }, [number]);

  // Sprawdzenie czy osiągnięto minimalną wartość zamówienia
  // Dla dostawy: sprawdza minimum dla wybranej strefy (deliveryAdress?.minimumOrderValue)
  // Dla odbioru osobistego: zawsze true
  const isOverMinOrderDeliveryPrice = useMemo(() => {
    if (deliveryType === 'SELF_PICKUP') {
      return true;
    }
    if (deliveryType === 'TO_THE_ADDRESS' && deliveryAdress?.minimumOrderValue !== undefined) {
      return total_ammount >= deliveryAdress.minimumOrderValue;
    }
    // Jeśli nie wybrano jeszcze strefy dostawy, sprawdź globalne minimum
    return total_ammount >= (selectedBranch?.minOrderDeliveryPrice || 0);
  }, [total_ammount, deliveryAdress, deliveryType, selectedBranch])

  useEffect(() => {
    if (selectedBranch) {
      setIsOpen(
        selectedBranch?.isOpen &&
        isCurrentTimeInSchedule(selectedBranch?.openingHours)
      );
    }
  }, [selectedBranch]);

  // 4. Handle redirect to configured main page (e.g. services) on initial landing
  useEffect(() => {
    if (!selectedBranch || currentPage.page !== 'dishes' || !isMain) return;

    const mainNavigate = selectedBranch?.config?.welcome_screen?.main_button?.navigate;
    if (mainNavigate && mainNavigate !== 'dishes') {
      const targetPage = legacyPathToPage(mainNavigate) as MenuPage;
      if (targetPage.page !== 'dishes') {
        navigateTo(targetPage);
      }
    }
  }, [selectedBranch, navigateTo, currentPage.page, isMain]);

  // Memoize context value to prevent unnecessary rerenders
  const contextValue = useMemo(
    () => ({
      cart,
      setCart,
      restaurantSlug,
      setRestaurantSlug,
      mealName,
      setMealName,
      selectedBranch,
      setSelectedBranch,
      deliveryType,
      setDeliveryType,
      additionalSections,
      setAdditionalSections,
      serviceData,
      setServiceData,
      serviceTime,
      setServiceTime,
      selectedCategory,
      setSelectedCategory,
      deliveryTimeType,
      setDeliveryTimeType,
      orderDate,
      setOrderDate,
      orderTime,
      setOrderTime,
      deliveryAdress,
      setDeliveryAdress,
      apertmenNumber,
      setApertmenNumber,
      deliveryNote,
      setDeliveryNote,
      selectedDeliveryMethod,
      setSelectedDeliveryMethod,
      name,
      setName,
      email,
      setEmail,
      number,
      setNumber,
      serviceResurces,
      setServiceResurces,
      isMain,
      setIsMain,
      isOpen,
      scroll,
      setScroll,
      isOverMinOrderDeliveryPrice,
      total_ammount,
      isEmailValid,
      emailError,
      isPhoneValid,
      phoneError,
      appliedCoupon,
      setAppliedCoupon,
      couponCode,
      setCouponCode,
      isOrder,
      setIsOrder,
      searchQuery,
      setSearchQuery,
      currentPage,
      navigateTo,
      goBack
    }),
    [
      cart,
      restaurantSlug,
      setRestaurantSlug,
      mealName,
      selectedBranch,
      deliveryType,
      additionalSections,
      serviceData,
      serviceTime,
      selectedCategory,
      deliveryTimeType,
      orderDate,
      orderTime,
      deliveryAdress,
      apertmenNumber,
      deliveryNote,
      selectedDeliveryMethod,
      name,
      email,
      number,
      serviceResurces,
      isMain,
      isOpen,
      scroll,
      isOverMinOrderDeliveryPrice,
      total_ammount,
      isEmailValid,
      emailError,
      isPhoneValid,
      phoneError,
      appliedCoupon,
      couponCode,
      isOrder,
      searchQuery,
      currentPage,
      navigateTo,
      goBack
    ]
  );

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};
